import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PAGE_REGISTRY from "./pageRegistry";
import { PreviewProvider } from "./PreviewModeContext";
import useEditAuth from "./useEditAuth";
import NavBar from "../NavBar";
import "./edit.css";

async function api(path, options = {}) {
  const res = await fetch(path, { credentials: "include", ...options });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    throw new Error((data && data.error) || `Request failed (${res.status})`);
  }
  return data;
}

function PreviewShell() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { checked, authenticated, login, logout } = useEditAuth();

  const page = PAGE_REGISTRY[pageId];

  const [slots, setSlots] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [folders, setFolders] = useState([]);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const loadContent = useCallback(async () => {
    if (!pageId || !PAGE_REGISTRY[pageId]) return;
    try {
      const data = await api(`/api/content/${encodeURIComponent(pageId)}`);
      setSlots(data.slots || {});
      setDirty(false);
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }, [pageId]);

  useEffect(() => {
    loadContent();
    setStatus("");
  }, [loadContent]);

  useEffect(() => {
    if (!authenticated) return;
    api("/api/images")
      .then((data) => setFolders(Array.isArray(data.folders) ? data.folders : []))
      .catch(() => {});
  }, [authenticated]);

  const setSlot = useCallback((id, slot) => {
    setSlots((prev) => {
      if (slot == null) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: slot };
    });
    setDirty(true);
    setStatus("");
  }, []);

  const handleSave = useCallback(async (slotsOverride) => {
    const payload = slotsOverride || slots;
    setSaving(true);
    setError("");
    try {
      await api(`/api/content/${encodeURIComponent(pageId)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slots: payload }),
      });
      setSlots(payload);
      setDirty(false);
      setStatus("已保存");
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [pageId, slots]);

  function handleDiscard() {
    if (dirty && !window.confirm("放弃未保存的更改？")) return;
    loadContent();
    setStatus("已恢复");
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    try {
      await login(loginPassword);
      setLoginPassword("");
    } catch (err) {
      setLoginError(err.message);
    }
  }

  if (!page) {
    return (
      <div className="edit-dashboard">
        <h1>页面不存在</h1>
        <p>
          <Link to="/edit/preview/home">返回主页预览</Link>
        </p>
      </div>
    );
  }

  if (!checked) {
    return <div className="edit-dashboard">加载中…</div>;
  }

  if (!authenticated) {
    return (
      <div className="edit-dashboard">
        <h1>网站编辑</h1>
        <p>请输入编辑密码以预览并编辑「{page.label}」</p>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            placeholder="编辑密码"
            autoComplete="current-password"
            required
          />
          <button type="submit">登录</button>
          {loginError && <p className="edit-error">{loginError}</p>}
        </form>
      </div>
    );
  }

  const PageComponent = page.component;

  return (
    <>
      <div className="preview-topbar">
        <select
          value={pageId}
          onChange={(e) => {
            if (dirty && !window.confirm("切换页面将丢失未保存的更改，继续？")) return;
            navigate(`/edit/preview/${e.target.value}`);
          }}
        >
          {Object.entries(PAGE_REGISTRY).map(([id, entry]) => (
            <option key={id} value={id}>
              {entry.label}
            </option>
          ))}
        </select>
        {dirty && <span className="dirty-flag">未保存</span>}
        {status && <span>{status}</span>}
        {error && <span className="dirty-flag">{error}</span>}
        <span className="spacer" />
        <Link to="/edit/images">图片管理</Link>
        <button type="button" onClick={handleDiscard} disabled={saving}>
          放弃更改
        </button>
        <button type="button" className="primary" onClick={handleSave} disabled={saving || !dirty}>
          {saving ? "保存中…" : "保存"}
        </button>
        <button type="button" onClick={logout}>
          退出登录
        </button>
      </div>
      <PreviewProvider slots={slots} setSlot={setSlot} folders={folders} savePage={handleSave}>
        <NavBar />
        <PageComponent />
      </PreviewProvider>
    </>
  );
}

export default PreviewShell;
